// Typo-tolerant matching shared by every search box in the app.
// Keeps exact/substring matches instant, and adds edit-distance tolerance so slight
// misspellings still hit (e.g. "Fredy" -> "Freddy", "Padvan" -> "Padovan").

// Bounded Damerau (optimal string alignment) distance — transpositions cost 1
// so common typos like "glroy" -> "glory" match. Returns max+1 once over budget.
function editDistance(a, b, max) {
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > max) return max + 1;
  if (la === 0) return lb;
  if (lb === 0) return la;
  const d = [];
  for (let i = 0; i <= la; i++) { d[i] = new Array(lb + 1); d[i][0] = i; }
  for (let j = 0; j <= lb; j++) d[0][j] = j;
  for (let i = 1; i <= la; i++) {
    let rowMin = Infinity;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, d[i - 2][j - 2] + 1);
      }
      d[i][j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
  }
  return d[la][lb];
}

// Allowed edit distance grows with token length.
function tolerance(len) {
  if (len <= 2) return 0;
  if (len <= 4) return 1;
  if (len <= 7) return 2;
  return 3;
}

function wordMatches(word, tok, maxD) {
  if (!word) return false;
  if (word.includes(tok) || tok.includes(word)) return true;
  if (maxD === 0) return false;
  // Compare against a same-length prefix so long words (e.g. "fredypadovan") still match "fredy".
  if (word.length > tok.length + maxD) {
    return editDistance(word.slice(0, tok.length), tok, maxD) <= maxD;
  }
  return editDistance(word, tok, maxD) <= maxD;
}

// True if `text` matches `query` allowing minor typos. Every query token must match
// some word in the text (order-independent). Empty query matches everything.
export function fuzzyMatch(text, query) {
  const t = String(text || "").toLowerCase();
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  if (t.includes(q)) return true; // fast path: exact substring
  const tokens = q.split(/\s+/).filter(Boolean);
  const words = t.split(/[^a-z0-9]+/i).filter(Boolean);
  return tokens.every((tok) => {
    if (t.includes(tok)) return true;
    const maxD = tolerance(tok.length);
    return words.some((w) => wordMatches(w, tok, maxD));
  });
}

// Relevance score of `text` for `query`. 0 = no match; higher = better. Used to rank search
// results so the strongest name/title match surfaces first. Exact and prefix matches beat
// substrings, which beat typo-tolerant fuzzy matches.
export function matchScore(text, query) {
  const t = String(text || "").toLowerCase().trim();
  const q = String(query || "").trim().toLowerCase();
  if (!q || !t) return 0;
  const words = t.split(/[^a-z0-9]+/i).filter(Boolean);
  if (t === q) return 100;
  if (words.some((w) => w === q)) return 95;
  if (words.some((w) => w.startsWith(q))) return 85;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  const tokens = q.split(/\s+/).filter(Boolean);
  const fuzzy = tokens.every((tok) => {
    if (t.includes(tok)) return true;
    const maxD = tolerance(tok.length);
    return words.some((w) => wordMatches(w, tok, maxD));
  });
  return fuzzy ? 40 : 0;
}

// True if every whitespace-separated token of `query` appears as a substring of `text`.
// Order-independent, no typo tolerance — used for free-text bodies (notes, cities) so long
// records don't produce fuzzy false positives.
export function substrAll(text, query) {
  const t = String(text || "").toLowerCase();
  const q = String(query || "").trim().toLowerCase();
  if (!q) return false;
  return q.split(/\s+/).filter(Boolean).every((tok) => t.includes(tok));
}
